import { BottomDrawer } from '@monorepo/shared/componentsV2/BottomDrawer';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { SitePickerForOrg } from '@monorepo/shared/componentsV2/SitePickerForOrg';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { header4 } from '@monorepo/shared/styles/text';
import { WorkflowStepResponse } from 'mapistry-shared';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { QueryResultsTable } from '@monorepo/shared/componentsV2/QueryResultsTable';
import { useQueryResults } from '@monorepo/shared/hooks/useQueryResults';
import { getStepDisplayInfo } from './stepUtils';
import { WorkflowPreviewEmptyState } from '../shared/WorkflowPreviewEmptyState';

const PreviewTitle = styled.div`
  ${header4}
`;

const SitePickerActionsGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

interface WorkflowStepPreviewBottomDrawerProps {
  previewStep?: WorkflowStepResponse;
  organizationId: string;
  onClose: () => void;
}

function WorkflowStepPreviewContents({
  organizationId,
  projectId,
  originId,
}: {
  organizationId: string;
  projectId: string;
  originId: string;
}) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [originId, projectId]);

  const {
    isLoading: queryIsLoading,
    isFetching: queryIsFetching,
    queryResult,
  } = useQueryResults({
    originId,
    organizationId,
    projectId,
    query: { steps: [] },
    requestedPage: page,
  });

  return (
    <QueryResultsTable
      isLoading={queryIsLoading || queryIsFetching}
      onPageChange={(newPage) => setPage(newPage)}
      organizationId={organizationId}
      originId={originId}
      projectId={projectId}
      queryResult={queryResult}
    />
  );
}

export function WorkflowStepPreviewBottomDrawer({
  previewStep,
  organizationId,
  onClose,
}: WorkflowStepPreviewBottomDrawerProps) {
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const { title } = previewStep
    ? getStepDisplayInfo(previewStep)
    : { title: undefined };

  const sitePicker = (
    <SitePickerForOrg
      organizationId={organizationId}
      name="preview-site"
      isLabelHidden
      dontSaveSpaceForErrorText
      isErrorTextHidden
      value={projectId}
      onChange={(e) => setProjectId(e.target.value as string)}
    />
  );

  function renderPreviewContent() {
    if (!previewStep) {
      return <EmptyState primaryText="No preview step selected" />;
    }
    if (!projectId) {
      return (
        <WorkflowPreviewEmptyState
          organizationId={organizationId}
          onSelectSite={setProjectId}
          secondaryText="This site will be used for all previews while editing the steps in this workflow."
        />
      );
    }
    return (
      <WorkflowStepPreviewContents
        organizationId={organizationId}
        projectId={projectId}
        originId={previewStep.outputDataset.id}
      />
    );
  }

  return (
    <BottomDrawer
      open={!!previewStep}
      leftActions={<PreviewTitle>{`${title} Step`}</PreviewTitle>}
      centerActions={
        <Button color="primary" variant="text" onClick={onClose}>
          Exit preview
        </Button>
      }
      rightActions={
        projectId ? (
          <SitePickerActionsGroup>
            <PreviewTitle>Previewing as</PreviewTitle>
            {sitePicker}
          </SitePickerActionsGroup>
        ) : null
      }
    >
      <ErrorBoundary key={`${projectId}-${previewStep?.id}`}>
        {renderPreviewContent()}
      </ErrorBoundary>
    </BottomDrawer>
  );
}
